import PropTypes from 'prop-types';
import React, { memo } from 'react';
import { Map, List } from 'immutable';
import dayjs from 'dayjs';

import { DATE_FORMAT } from '../../utils/dateFormat';
import Button from '../helpers/Button';
import { AppPage } from '../AppPage';
import { Card } from '../helpers/Card';
import { GridCard } from '../helpers/GridCard';
import { SettingPanel } from './SettingPanel';
import { Counter } from './SettingPanel/Counter';
import { TopMenu } from './Grid/TopMenu';
import Grid from './Grid';

import style from './style.module.css';

/**
 * Stateless component for next actionList.
 *
 * @param props {Object}.
 * @param props.userName {Immutable.Map} User name.
 * @param props.actions {Immutable.List} List of next action items.
 * @param props.settings {Immutable.Map} User's filering and sorting settings information.
 * @param props.common {Immutable.Map} Common information.
 * @param props.selected {Immutable.List} List of selected next action items.
 * @param props.disabled {Boolean} If true, disabled top drop down menu.
 * @param props.exportingDisabled {Boolean} If true, disable exporting button.
 * @param props.actionConfigs {Immutable.List} Actions of available actions.
 * @param props.setGridReference {Function} To hold grid ref.
 * @param props.sorts {Immutable.Map} List of sorting items.
 * @param props.typeFilters {Immutable.Map} Approach type filters data.
 * @param props.mailFilters {Immutable.Map} Mailing filters data.
 * @param props.activityFilters {Immutable.Map} Activity filters data.
 * @param props.userFilters {Immutable.Map} User type filters data.
 * @param props.onChangeSort {Function} To handle changing sorting field.
 * @param props.onChangeOrder {Function} To handle changing sorting order.
 * @param props.onSelectAll {Function} To select all next actions.
 * @param props.onDeselectAll {Function} To deselect all next actions.
 * @param props.onRowSelected {Function} Handle row selection.
 * @param props.onExportClick {Function} Handle exporting action actions to excel.
 * @param props.onMenuSelect {Function} Handle context menu item's click event.
 * @param props.onFilterChange {Function} To handle changing filtering field.
 * @param props.onBLClick {Function} To handle setting backlog.
 */
const ActionComponent = ({
  backloggedActivities,
  userName,
  actions,
  common,
  selected,
  disabled,
  exportingDisabled,
  actionConfigs,
  setGridReference,
  sorts,
  typeFilters,
  mailFilters,
  activityFilters,
  userFilters,
  onSelectAll,
  onDeselectAll,
  onChangeSort,
  onChangeOrder,
  onRowSelected,
  onExportClick,
  onMenuSelect,
  onFilterChange,
  onBLClick,
}) => {
  const isFetchingAction = common.get('loadingAction');
  const isFetchingSetting = common.get('loadingSetting');
  const total = common.get('total');

  const toolbar = (
    <>
      <Counter showing={actions.size} total={total} />
      <div className={style.toolbar}>
        <TopMenu
          actionConfigs={actionConfigs}
          disabled={disabled}
          onChange={onMenuSelect}
          onDeselectAll={onDeselectAll}
          onSelectAll={onSelectAll}
          selected={selected}
        />
        <Button disabled={exportingDisabled} onClick={onExportClick}>
          <i aria-hidden="true" className="fa fa-download" /> Export
        </Button>
      </div>
    </>
  );

  return (
    <AppPage overline={`${userName} · ${dayjs().format(DATE_FORMAT)}`} title="Next Actions" toolbar={toolbar}>
      <div className={style.layout}>
        <GridCard className={style.grid}>
          <Grid
            actionConfigs={actionConfigs}
            backloggedActivities={backloggedActivities}
            isFetching={isFetchingAction}
            list={actions}
            onBLClick={onBLClick}
            onMenuSelect={onMenuSelect}
            onRowSelected={onRowSelected}
            setGridReference={setGridReference}
          />
        </GridCard>
        <Card className={style.settings}>
          <SettingPanel
            activityFilters={activityFilters}
            isFetching={isFetchingSetting}
            mailFilters={mailFilters}
            onChangeOrder={onChangeOrder}
            onChangeSort={onChangeSort}
            onFilterChange={onFilterChange}
            sorts={sorts}
            typeFilters={typeFilters}
            userFilters={userFilters}
          />
        </Card>
      </div>
    </AppPage>
  );
};

ActionComponent.propTypes = {
  actionConfigs: PropTypes.instanceOf(List).isRequired,
  actions: PropTypes.instanceOf(List).isRequired,
  activityFilters: PropTypes.instanceOf(Map),
  common: PropTypes.instanceOf(Map).isRequired,
  disabled: PropTypes.bool.isRequired,
  exportingDisabled: PropTypes.bool.isRequired,
  mailFilters: PropTypes.instanceOf(Map),
  onBLClick: PropTypes.func.isRequired,
  onChangeOrder: PropTypes.func.isRequired,
  onChangeSort: PropTypes.func.isRequired,
  onDeselectAll: PropTypes.func.isRequired,
  onExportClick: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onMenuSelect: PropTypes.func.isRequired,
  onRowSelected: PropTypes.func.isRequired,
  onSelectAll: PropTypes.func.isRequired,
  selected: PropTypes.instanceOf(List).isRequired,
  setGridReference: PropTypes.func.isRequired,
  sorts: PropTypes.instanceOf(List),
  typeFilters: PropTypes.instanceOf(Map),
  userFilters: PropTypes.instanceOf(Map),
  userName: PropTypes.string.isRequired,
};

export const Action = memo(ActionComponent);
